import { z } from 'zod';
import { DeletionScheduleView, dataExportArchiveSchema } from './data-rights.ts';
import {
  ModerationAction,
  ModerationSubject,
  ModerationVerdict,
  StaffAuditAction,
  StaffRole,
} from './enums.ts';

/**
 * Admin-console WIRE CONTRACTS — the Zod-backed source of truth for the
 * staff-scoped `api` surface (`/admin/*`), shared api ↔ backend ↔ admin SPA. The
 * backend view-mappers PRODUCE these shapes; the admin client RE-VALIDATES them
 * at the network boundary. Staff auth + data are SEPARATE from consumers: no
 * consumer field leaks in here. Money stays in micros; timestamps are ISO
 * strings rendered in the operator's timezone.
 */

/** Staff role hierarchy — a higher rank subsumes every lower privilege. */
export const STAFF_ROLE_RANK: Record<StaffRole, number> = {
  support: 1,
  moderator: 2,
  admin: 3,
};

/**
 * True when `role` meets or exceeds `min` on the {@link STAFF_ROLE_RANK} ladder
 * — the ONE predicate both the edge role-gate middleware and every service's
 * defense-in-depth re-check share (DRY), so "moderator+" means the same thing
 * on the wire and in the domain.
 */
export function staffRoleAtLeast(role: StaffRole, min: StaffRole): boolean {
  return STAFF_ROLE_RANK[role] >= STAFF_ROLE_RANK[min];
}

/** The signed-in operator the SPA renders (no secrets — never the password hash). */
export const StaffSessionView = z.object({
  id: z.string().min(1),
  email: z.string().email(),
  role: StaffRole,
});
export type StaffSessionView = z.infer<typeof StaffSessionView>;

/** `POST /admin/auth/login` (and `/google`) success — the token plus who it is. */
export const StaffLoginView = z.object({
  /** The staff bearer token — held in memory by the SPA, sent as `Authorization`. */
  token: z.string().min(1),
  /** Token lifetime in seconds. */
  expiresIn: z.number().int().positive(),
  staff: StaffSessionView,
});
export type StaffLoginView = z.infer<typeof StaffLoginView>;

/**
 * What FORM the reviewable evidence behind a held item takes — it decides how
 * the console renders a reveal (a text block vs. a pixel surface) and therefore
 * how hazardous the reveal is. Admin-wire-local (it exists only to shape this
 * contract), so it lives beside the views it types rather than in `enums.ts`.
 */
export const ModerationEvidenceKind = z.enum(['text', 'image', 'video', 'none']);
export type ModerationEvidenceKind = z.infer<typeof ModerationEvidenceKind>;

/**
 * The evidence DESCRIPTOR carried on a queue row — deliberately NOT the evidence
 * itself. The list payload never contains offending text or a media URL: the
 * bytes are fetched per item, on demand, through the audited reveal endpoint
 * (`POST /admin/moderation/evidence`). Three reasons this split is the contract:
 *
 *  1. **Attribution.** A reveal must be pinned to one operator, one item, one
 *     instant. A blob delivered with the list cannot be attributed to anything.
 *  2. **Blast radius.** Short-TTL presigned URLs are only short-TTL if they are
 *     minted at the moment of viewing, not once per queue poll for every row.
 *  3. **Leak surface.** Red-line content never enters a payload that is fanned
 *     out, cached, or logged as "the queue".
 */
export const ModerationEvidenceRefView = z.object({
  kind: ModerationEvidenceKind,
  /** True when the reveal endpoint can actually serve this item's evidence. */
  available: z.boolean(),
  /**
   * True when viewing is itself hazardous (a suspected-minor / real-person
   * item). The SERVER is authority here so the console cannot accidentally
   * un-gate; the console additionally ORs its own red-line read (fail-closed).
   */
  hazardous: z.boolean(),
});
export type ModerationEvidenceRefView = z.infer<typeof ModerationEvidenceRefView>;

/**
 * The descriptor assumed when a row carries none — nothing to show, and treated
 * as hazardous so an absent descriptor can never open the reveal gate. Every
 * consumer must read {@link moderationEvidenceRef}, never `item.evidence` raw.
 */
export const FAIL_CLOSED_EVIDENCE: ModerationEvidenceRefView = {
  kind: 'none',
  available: false,
  hazardous: true,
};

/** One held item awaiting a human decision in the moderation queue. */
export const ModerationQueueItemView = z.object({
  id: z.string().min(1),
  accountId: z.string().min(1),
  userId: z.string().min(1),
  subject: ModerationSubject,
  /** The classified artifact (message / asset / upload / persona); null if inline. */
  refId: z.string().nullable(),
  verdict: ModerationVerdict,
  /** Classifier category → score map. */
  categories: z.record(z.string(), z.unknown()),
  /** The classifier model that produced the verdict; null if unrecorded. */
  model: z.string().nullable(),
  createdAt: z.string(),
  /**
   * Whether reviewable evidence exists and how it must be gated — a DESCRIPTOR,
   * never the content ({@link ModerationEvidenceRefView}). Optional on the wire
   * so a producer that has not yet resolved it omits the field; readers resolve
   * it through {@link moderationEvidenceRef}, which substitutes the fail-closed
   * descriptor. Absent ⇒ "no evidence, treat as hazardous" ⇒ the console
   * refuses the decisions that require sighted review.
   */
  evidence: ModerationEvidenceRefView.optional(),
});
export type ModerationQueueItemView = z.infer<typeof ModerationQueueItemView>;

/** The evidence descriptor for a row, fail-closed when the producer omitted it. */
export function moderationEvidenceRef(
  item: Pick<ModerationQueueItemView, 'evidence'>,
): ModerationEvidenceRefView {
  return item.evidence ?? FAIL_CLOSED_EVIDENCE;
}

/**
 * Hard bound on the opaque queue cursor as it travels on the wire. The encoding
 * is a fixed-width triple, so anything materially longer is not a cursor we
 * minted — rejecting it at the query-string boundary keeps an unbounded string
 * out of the decoder (and out of the request log) entirely.
 */
export const MODERATION_CURSOR_MAX_LENGTH = 256;

/** The `GET /admin/moderation/queue` payload — one page of held items, oldest first. */
export const ModerationQueueView = z.object({
  items: z.array(ModerationQueueItemView),
  /**
   * The OPAQUE cursor for the next page, or `null` when the queue is drained.
   * Pass it back verbatim as `?cursor=` — never parse, slice, or synthesize one;
   * the server refuses any value it did not mint.
   *
   * REQUIRED-nullable, not optional, and that is the whole point of this field.
   * The defect it closes is held items past the page limit being unreachable
   * FOREVER, and a silently-absent "there is more" signal is that same defect
   * wearing a different hat: the console would render a full page, see no cursor,
   * and tell a moderator the compliance queue is clear while red-line candidates
   * sit behind it. A producer must say, out loud, that it is done. api + console
   * ship in one release (GitOps), so there is no skew window worth trading that
   * signal for.
   */
  nextCursor: z.string().nullable(),
});
export type ModerationQueueView = z.infer<typeof ModerationQueueView>;

/**
 * The audited reveal payload — the ACTUAL evidence behind one held item, served
 * only by `POST /admin/moderation/evidence` and only to a `moderator`+. Every
 * successful response corresponds to one `staff_audit_log` entry: this operator
 * viewed this item at this instant. `revealedBy`/`revealedAt` are echoed back so
 * the console can tell the operator, in the same breath as showing the content,
 * that the viewing is on the record.
 *
 * Exactly one payload channel is populated: `text` for a text subject, `url` for
 * a pixel subject. `url` is a SHORT-TTL, staff-scoped presigned GET — it must
 * never be persisted, copied into a ticket, or logged; `expiresAt` is when it
 * stops working and the console must re-reveal (re-audit) to see it again.
 */
export const ModerationEvidenceView = z
  .object({
    eventId: z.string().min(1),
    kind: ModerationEvidenceKind,
    /** Verbatim offending text (prompt / message / persona), or null for pixels. */
    text: z.string().nullable(),
    /** Short-TTL presigned GET for the offending asset, or null for text. */
    url: z.string().nullable(),
    /** ISO instant `url` expires; null when there is no url. */
    expiresAt: z.string().nullable(),
    /** Server-authoritative: this reveal must render behind the blur/confirm gate. */
    hazardous: z.boolean(),
    /** ISO instant the reveal was recorded against the operator. */
    revealedAt: z.string(),
    /** The staff id the reveal was attributed to (from the token, never the body). */
    revealedBy: z.string().min(1),
  })
  .superRefine((value, ctx) => {
    if (value.kind === 'text' && value.text === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['text'],
        message: 'text evidence must carry the offending text',
      });
    }
    if ((value.kind === 'image' || value.kind === 'video') && value.url === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['url'],
        message: 'pixel evidence must carry a presigned url',
      });
    }
  });
export type ModerationEvidenceView = z.infer<typeof ModerationEvidenceView>;

/**
 * What the decision did to the HELD ARTIFACT behind the item — the bytes or the
 * companion the verdict was always supposed to govern.
 *
 *  - `published` — an `approve` released the held artifact: the retained object
 *    became a served `media_assets` row (and, on a build, the companion's face).
 *  - `purged` — a `reject`/`ban` destroyed the retained bytes and reverted the
 *    state they were holding open.
 *  - `none` — nothing to act on: a text subject (no artifact today), or an
 *    artifact another operator already resolved.
 *  - `failed` — the verdict COMMITTED but the artifact step did not. The item is
 *    out of the queue and the trail carries a `moderation.artifact.failed` entry;
 *    the bytes still need a sweep. Surfaced so the console can say so out loud
 *    rather than implying the artifact was handled.
 */
export const ModerationArtifactOutcome = z.enum(['published', 'purged', 'none', 'failed']);
export type ModerationArtifactOutcome = z.infer<typeof ModerationArtifactOutcome>;

/** The outcome of a moderation decision — the new verdict + whether it moved. */
export const ModerationActionResultView = z.object({
  id: z.string().min(1),
  action: ModerationAction,
  verdict: ModerationVerdict,
  /** True when THIS call transitioned it out of `review` (idempotent-safe). */
  changed: z.boolean(),
  /** Set when the action banned the offender's account. */
  accountDisabled: z.boolean(),
  /**
   * What happened to the held artifact. Optional on the wire so an older producer
   * that only flips verdicts still validates; readers resolve it through
   * {@link moderationArtifactOutcome}, which reports the honest `'none'`.
   */
  artifact: ModerationArtifactOutcome.optional(),
});
export type ModerationActionResultView = z.infer<typeof ModerationActionResultView>;

/** The artifact outcome for a decision, defaulting to "nothing was acted on". */
export function moderationArtifactOutcome(
  result: Pick<ModerationActionResultView, 'artifact'>,
): ModerationArtifactOutcome {
  return result.artifact ?? 'none';
}

/** The account + owner a user lookup resolves — the support view (no secrets). */
export const UserLookupView = z.object({
  user: z.object({
    id: z.string().min(1),
    accountId: z.string().min(1),
    email: z.string().email(),
    displayName: z.string(),
    ageOk: z.boolean(),
    referralCode: z.string(),
    createdAt: z.string(),
  }),
  account: z.object({
    id: z.string().min(1),
    tier: z.string(),
    creditBalanceMicros: z.number(),
    /** ISO timestamp when the account was hard-disabled (banned), else null. */
    disabledAt: z.string().nullable(),
  }),
  /** How many companions the user has built. */
  companionCount: z.number().int().nonnegative(),
});
export type UserLookupView = z.infer<typeof UserLookupView>;

/**
 * The result of a data-rights action (export / delete) an operator executed on a
 * consumer's behalf, plus the audit acknowledgement.
 *
 * Exactly ONE of `archive` / `deletion` is populated, keyed by `action`:
 * `user.export` carries the assembled archive (what the operator hands to the
 * subject), `user.delete` carries the soft-disable schedule. Both were previously
 * absent — the endpoints wrote an audit row and did nothing else, so an operator
 * honouring a GDPR erasure believed it had run. The payload is what makes the
 * acknowledgement checkable rather than a promise.
 */
export const DataRightsAckView = z.object({
  userId: z.string().min(1),
  action: StaffAuditAction,
  acknowledgedAt: z.string(),
  // Both are REQUIRED-nullable rather than optional: the console re-validates
  // this envelope, and a silently-absent payload is exactly the shape the old
  // do-nothing acknowledgement had. api + console ship in one release (GitOps),
  // so there is no skew window worth trading that signal for.
  /** The assembled export archive — present only for `user.export`. */
  archive: dataExportArchiveSchema.nullable(),
  /** The soft-disable + grace schedule — present only for `user.delete`. */
  deletion: DeletionScheduleView.nullable(),
});
export type DataRightsAckView = z.infer<typeof DataRightsAckView>;

/** One referrer's rev-share payout total — the read-first payouts audit row. */
export const PayoutRowView = z.object({
  referrerUserId: z.string().min(1),
  /** The referrer's email when resolvable, else null (a dangling id). */
  email: z.string().nullable(),
  earnedMicros: z.number(),
  earningCount: z.number().int().nonnegative(),
});
export type PayoutRowView = z.infer<typeof PayoutRowView>;

/** The `GET /admin/payouts` payload — per-referrer totals + the grand total. */
export const PayoutsView = z.object({
  rows: z.array(PayoutRowView),
  totalMicros: z.number(),
  totalCount: z.number().int().nonnegative(),
});
export type PayoutsView = z.infer<typeof PayoutsView>;
